import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Trophy, CaretDown, CaretRight, Warning, CheckCircle, XCircle, ChartBar, ArrowRight, Equals } from '@phosphor-icons/react';
import { toast } from 'sonner';
import WindowNarrative from '../components/WindowNarrative';
import { NARRATIVES } from '../components/windowNarratives';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RULE_LABELS = {
  sum: 'Sum Range',
  odd_even: 'Odd/Even',
  low_high: 'Low/High',
  consecutive: 'Consecutive',
  range_pattern: 'Range Pattern',
  shared_with_prev: 'Shared w/ Prev',
  sign_pattern: 'Sign Direction',
  spread: 'Spread',
  ls_sum: 'LS Sum',
  decade_cluster: 'Decade Cluster',
};

const RULE_ORDER = ['sum', 'odd_even', 'low_high', 'consecutive', 'range_pattern', 'shared_with_prev', 'sign_pattern', 'spread', 'ls_sum', 'decade_cluster'];

function formatAmount(amt) {
  if (!amt) return '-';
  if (typeof amt === 'string') return amt;
  return '£' + amt.toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

function BallChip({ n, isLS }) {
  return (
    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-mono font-bold text-xs ${
      isLS ? 'bg-amber-400 text-amber-900' : 'bg-slate-700 text-white'
    }`}>{n}</span>
  );
}

function RuleCell({ rule }) {
  if (!rule) return <td className="px-2 py-1.5 text-center text-gray-300 text-xs">-</td>;
  return (
    <td className={`px-2 py-1.5 text-center text-xs font-mono ${rule.violated ? 'bg-red-100 text-red-700 font-bold' : 'text-gray-500'}`}>
      {typeof rule.value === 'number' ? rule.value : String(rule.value)}
      {rule.violated && <XCircle size={11} weight="bold" className="inline ml-0.5 text-red-500" />}
    </td>
  );
}

function SegmentCard({ segment, idx }) {
  const [expanded, setExpanded] = useState(false);
  const { winner_a, winner_b, draws_between, draw_analyses, violation_summary } = segment;

  const totalViolations = Object.values(violation_summary).reduce((a, b) => a + b, 0);
  const topViolated = Object.entries(violation_summary).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-testid={`segment-${idx}`}>
      {/* Header */}
      <button onClick={() => setExpanded(!expanded)} className="w-full px-5 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors text-left">
        <div className="flex items-center gap-1">
          {expanded ? <CaretDown size={16} className="text-gray-400" /> : <CaretRight size={16} className="text-gray-400" />}
        </div>

        <div className="flex-1 flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Trophy size={16} weight="fill" className="text-amber-500" />
            <span className="text-gray-900 font-bold text-sm">{winner_a.draw_date}</span>
            <span className="text-emerald-600 font-bold text-sm">{formatAmount(winner_a.amount)}</span>
          </div>
          <span className="text-gray-300 text-xs">to</span>
          <div className="flex items-center gap-2">
            <Trophy size={16} weight="fill" className="text-amber-500" />
            <span className="text-gray-900 font-bold text-sm">{winner_b.draw_date}</span>
            <span className="text-emerald-600 font-bold text-sm">{formatAmount(winner_b.amount)}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="bg-gray-100 px-3 py-1.5 rounded text-xs font-bold text-gray-600">{draws_between} draws</span>
          <span className={`px-3 py-1.5 rounded text-xs font-bold ${totalViolations > 10 ? 'bg-red-100 text-red-700' : totalViolations > 5 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
            {totalViolations} violations
          </span>
        </div>
      </button>

      {expanded && (
        <div className="px-5 pb-5 space-y-4 bg-gray-50">
          {topViolated.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <span className="text-[10px] text-gray-400 font-bold uppercase pt-1">Most Violated:</span>
              {topViolated.map(([rule, count]) => (
                <span key={rule} className="bg-red-100 text-red-700 px-2.5 py-1 rounded-full text-xs font-bold">
                  {RULE_LABELS[rule] || rule}: {count}x
                </span>
              ))}
            </div>
          )}

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={14} weight="fill" className="text-amber-500" />
              <span className="text-emerald-800 font-bold text-sm">WINNER: {winner_a.draw_date} — {formatAmount(winner_a.amount)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {winner_a.balls?.map((b, i) => <BallChip key={`a-b-${b}-${i}`} n={b} />)}
              <span className="text-gray-300 mx-1">|</span>
              {winner_a.lucky_stars?.map((b, i) => <BallChip key={`a-ls-${b}-${i}`} n={b} isLS />)}
              {winner_a.rules && (
                <span className="ml-3 text-xs text-gray-500">Sum: {winner_a.rules.sum?.value} | {winner_a.rules.odd_even?.value} | Spread: {winner_a.rules.spread?.value}</span>
              )}
            </div>
          </div>

          {draw_analyses.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-300 bg-gray-100">
                    <th className="px-2 py-2 text-left text-gray-600 font-bold">Date</th>
                    <th className="px-2 py-2 text-left text-gray-600 font-bold">Balls</th>
                    <th className="px-2 py-2 text-left text-gray-600 font-bold">LS</th>
                    {RULE_ORDER.map(r => (
                      <th key={r} className="px-2 py-2 text-center text-gray-600 font-bold whitespace-nowrap">{RULE_LABELS[r]}</th>
                    ))}
                    <th className="px-2 py-2 text-center text-red-600 font-bold">Violations</th>
                  </tr>
                </thead>
                <tbody>
                  {draw_analyses.map((da, j) => (
                    <tr key={`da-${da.draw_date}-${j}`} className={`border-b border-gray-100 ${da.violated_count > 2 ? 'bg-red-50' : 'bg-white'}`}>
                      <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap font-medium">{da.draw_date}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-0.5">
                          {da.balls.map((b, i) => <span key={`da-b-${b}-${i}`} className="bg-gray-200 px-1.5 rounded text-gray-700 font-mono text-[11px]">{b}</span>)}
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-0.5">
                          {da.lucky_stars.map((b, i) => <span key={`da-ls-${b}-${i}`} className="bg-amber-100 px-1.5 rounded text-amber-700 font-mono text-[11px]">{b}</span>)}
                        </div>
                      </td>
                      {RULE_ORDER.map(r => <RuleCell key={r} rule={da.rules[r]} />)}
                      <td className="px-2 py-1.5 text-center">
                        {da.violated_count > 0 ? (
                          <span className={`font-bold ${da.violated_count >= 3 ? 'text-red-600' : da.violated_count >= 2 ? 'text-amber-600' : 'text-gray-500'}`}>
                            {da.violated_count}
                          </span>
                        ) : (
                          <CheckCircle size={14} className="inline text-emerald-500" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-2">
              <Trophy size={14} weight="fill" className="text-amber-500" />
              <span className="text-emerald-800 font-bold text-sm">PREVIOUS WINNER: {winner_b.draw_date} — {formatAmount(winner_b.amount)}</span>
            </div>
            <div className="flex items-center gap-1.5">
              {winner_b.balls?.map((b, i) => <BallChip key={`b-b-${b}-${i}`} n={b} />)}
              <span className="text-gray-300 mx-1">|</span>
              {winner_b.lucky_stars?.map((b, i) => <BallChip key={`b-ls-${b}-${i}`} n={b} isLS />)}
              {winner_b.rules && (
                <span className="ml-3 text-xs text-gray-500">Sum: {winner_b.rules.sum?.value} | {winner_b.rules.odd_even?.value} | Spread: {winner_b.rules.spread?.value}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CurrentDrawAnalysis() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/analysis/winner-current-draw`);
        setData(res.data);
      } catch { toast.error('Failed to fetch current draw analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-white/50 text-center py-10">Analyzing latest draw...</div>;
  if (!data) return null;

  const { latest_draw, prev_draw, rules, violated_rules, rule_history } = data;
  const since_last_winner = data.since_last_winner || { draws_count: 0, last_winner_date: latest_draw?.draw_date || '', streak_violations: {}, draw_details: [], averages: null };

  return (
    <div className="space-y-6">
      {/* Latest draw info */}
      <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-testid="current-draw-info">
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
              <ChartBar size={18} weight="fill" className="text-blue-500" />
              Latest Draw: {latest_draw.draw_date}
              {latest_draw.has_winner && <span className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs font-bold ml-2">WINNER {formatAmount(latest_draw.winner_amount)}</span>}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">Compared against previous draw: {prev_draw.draw_date}</p>
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            {latest_draw.balls.map((b, i) => <BallChip key={`l-b-${b}-${i}`} n={b} />)}
            <span className="text-gray-300 mx-1">|</span>
            {latest_draw.lucky_stars.map((b, i) => <BallChip key={`l-ls-${b}-${i}`} n={b} isLS />)}
          </div>
        </div>

        {/* Since last winner streak — per draw details */}
        <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-4 mb-3">
            <div>
              <span className="text-xs text-blue-700 font-bold uppercase">Since Last Winner ({since_last_winner.last_winner_date})</span>
              <p className="text-gray-900 font-bold text-2xl">{since_last_winner.draws_count} draws</p>
            </div>
            {since_last_winner.draws_count === 0 ? (
              <div className="flex-1 text-xs text-emerald-700 font-semibold bg-emerald-50 border border-emerald-200 rounded px-3 py-2">
                🎉 The latest draw itself was a jackpot winner — no streak to analyse yet.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5 flex-1">
                {Object.entries(since_last_winner.streak_violations || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([r, count]) => (
                  <span key={r} className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs font-bold border border-red-200">
                    {RULE_LABELS[r]}: {count}x
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Per-draw violation table */}
          {since_last_winner.draw_details?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-blue-200 bg-blue-100/50">
                    <th className="px-2 py-2 text-left text-blue-800 font-bold">Date</th>
                    <th className="px-2 py-2 text-left text-blue-800 font-bold">Balls</th>
                    <th className="px-2 py-2 text-left text-blue-800 font-bold">LS</th>
                    {RULE_ORDER.map(r => (
                      <th key={r} className="px-2 py-2 text-center text-blue-800 font-bold whitespace-nowrap">{RULE_LABELS[r]}</th>
                    ))}
                    <th className="px-2 py-2 text-center text-red-600 font-bold">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {since_last_winner.draw_details.map((dd, j) => (
                    <tr key={`dd-${dd.draw_date}-${j}`} className={`border-b border-gray-100 ${dd.violated_rules.length >= 3 ? 'bg-red-50' : 'bg-white'}`}>
                      <td className="px-2 py-1.5 text-gray-800 whitespace-nowrap font-bold">{dd.draw_date}</td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-0.5">
                          {dd.balls.map((b, i) => <span key={`dd-b-${b}-${i}`} className="bg-gray-200 px-1 rounded text-gray-700 font-mono text-[11px]">{b}</span>)}
                        </div>
                      </td>
                      <td className="px-2 py-1.5">
                        <div className="flex gap-0.5">
                          {dd.lucky_stars.map((b, i) => <span key={`dd-ls-${b}-${i}`} className="bg-amber-100 px-1 rounded text-amber-700 font-mono text-[11px]">{b}</span>)}
                        </div>
                      </td>
                      {RULE_ORDER.map(r => {
                        const rv = dd.rules?.[r];
                        return (
                          <td key={r} className={`px-2 py-1.5 text-center text-xs font-mono ${rv?.violated ? 'bg-red-100 text-red-700 font-bold' : 'text-gray-400'}`}>
                            {rv ? (typeof rv.value === 'number' ? rv.value : String(rv.value)) : '-'}
                            {rv?.violated && <XCircle size={10} weight="bold" className="inline ml-0.5 text-red-500" />}
                          </td>
                        );
                      })}
                      <td className="px-2 py-1.5 text-center">
                        <span className={`font-bold ${dd.violated_rules.length >= 3 ? 'text-red-600' : dd.violated_rules.length > 0 ? 'text-amber-600' : 'text-emerald-500'}`}>
                          {dd.violated_rules.length}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Averages across streak draws — only render when there ARE post-winner draws to average */}
      {since_last_winner.averages && !Array.isArray(since_last_winner.averages) && since_last_winner.averages.position_avg && (
        <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-testid="streak-averages">
          <h2 className="text-base font-bold text-gray-900 mb-4">Averages Across Last {since_last_winner.draws_count} Draws (Since Winner {since_last_winner.last_winner_date})</h2>

          {/* Position averages */}
          <div className="grid grid-cols-7 gap-3 mb-5">
            {Object.entries(since_last_winner.averages.position_avg).map(([pos, avg]) => (
              <div key={pos} className={`rounded-lg p-3 text-center border ${pos.startsWith('LS') ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                <p className={`text-xs font-bold ${pos.startsWith('LS') ? 'text-amber-700' : 'text-blue-700'}`}>{pos}</p>
                <p className="text-2xl font-bold text-gray-900">{avg}</p>
                <p className="text-[10px] text-gray-400">avg</p>
              </div>
            ))}
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500 font-bold uppercase">Sum Avg</p>
              <p className="text-xl font-bold text-gray-900">{since_last_winner.averages.sum_avg}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500 font-bold uppercase">Odd/Even Avg</p>
              <p className="text-xl font-bold text-gray-900">{since_last_winner.averages.odd_avg}O / {since_last_winner.averages.even_avg}E</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500 font-bold uppercase">Low/High Avg</p>
              <p className="text-xl font-bold text-gray-900">{since_last_winner.averages.low_avg}L / {since_last_winner.averages.high_avg}H</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500 font-bold uppercase">Spread Avg</p>
              <p className="text-xl font-bold text-gray-900">{since_last_winner.averages.spread_avg}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500 font-bold uppercase">LS Sum Avg</p>
              <p className="text-xl font-bold text-gray-900">{since_last_winner.averages.ls_sum_avg}</p>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500 font-bold uppercase">Consec Avg</p>
              <p className="text-xl font-bold text-gray-900">{since_last_winner.averages.consec_avg}</p>
            </div>
          </div>

          {/* Sign Direction averages */}
          <div>
            <p className="text-xs text-gray-500 font-bold uppercase mb-2">Sign Direction Average (+ / - / = per position)</p>
            <div className="grid grid-cols-5 gap-3">
              {(since_last_winner.averages.sign_direction || []).map((sd) => (
                <div key={sd.pos} className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                  <p className="text-xs font-bold text-gray-700 mb-1">{sd.pos}</p>
                  <div className="flex justify-center gap-2 text-sm font-mono">
                    <span className="text-emerald-600 font-bold">+{sd.plus}</span>
                    <span className="text-red-600 font-bold">-{sd.minus}</span>
                    <span className="text-gray-400">={sd.zero}</span>
                  </div>
                  <div className="mt-1.5 flex h-2 rounded-full overflow-hidden bg-gray-200">
                    {sd.plus + sd.minus + sd.zero > 0 && (
                      <>
                        <div className="bg-emerald-500" style={{ width: `${sd.plus / (sd.plus + sd.minus + sd.zero) * 100}%` }} />
                        <div className="bg-red-500" style={{ width: `${sd.minus / (sd.plus + sd.minus + sd.zero) * 100}%` }} />
                        <div className="bg-gray-400" style={{ width: `${sd.zero / (sd.plus + sd.minus + sd.zero) * 100}%` }} />
                      </>
                    )}
                  </div>
                  <p className={`text-xs font-bold mt-1 ${sd.dominant === '+' ? 'text-emerald-600' : sd.dominant === '-' ? 'text-red-600' : 'text-gray-500'}`}>
                    Dominant: {sd.dominant}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm" data-testid="current-draw-rules">
        <h2 className="text-base font-bold text-gray-900 mb-1">Rules Flouted in {latest_draw.draw_date} — Historical Draw Outcome</h2>
        <p className="text-xs text-gray-400 mb-4">For each rule: current draw status + when this rule was violated historically, what % of those draws were winners vs non-winners</p>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-3 py-2 text-left text-gray-600 font-bold text-xs">Rule</th>
                <th className="px-3 py-2 text-center text-gray-600 font-bold text-xs">Current Value</th>
                <th className="px-3 py-2 text-center text-gray-600 font-bold text-xs">Status</th>
                <th className="px-3 py-2 text-center text-gray-600 font-bold text-xs">Historical Violations</th>
                <th className="px-3 py-2 text-center text-amber-600 font-bold text-xs">Winners When Violated</th>
                <th className="px-3 py-2 text-center text-gray-600 font-bold text-xs">Non-Winners When Violated</th>
                <th className="px-3 py-2 text-center text-amber-600 font-bold text-xs">% Winners</th>
                <th className="px-3 py-2 text-center text-gray-600 font-bold text-xs">% Non-Winners</th>
                <th className="px-3 py-2 text-left text-gray-600 font-bold text-xs">%</th>
              </tr>
            </thead>
            <tbody>
              {rule_history.map((rh, i) => {
                const winPct = rh.pct_winners_when_violated;
                return (
                  <tr key={rh.rule} className={`border-b border-gray-100 ${rh.is_violated_now ? 'bg-red-50' : ''}`} data-testid={`current-rule-${rh.rule}`}>
                    <td className="px-3 py-2 font-bold text-gray-900">{RULE_LABELS[rh.rule] || rh.rule}</td>
                    <td className="px-3 py-2 text-center font-mono text-gray-600">
                      {typeof rh.current_value === 'number' ? rh.current_value : String(rh.current_value || '-')}
                    </td>
                    <td className="px-3 py-2 text-center">
                      {rh.is_violated_now
                        ? <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs font-bold">VIOLATED</span>
                        : <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-bold">OK</span>}
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-gray-500">{rh.total_draws_violated}</td>
                    <td className="px-3 py-2 text-center font-mono font-bold text-amber-600">{rh.winners_that_violated}</td>
                    <td className="px-3 py-2 text-center font-mono text-gray-400">{rh.non_winners_that_violated}</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`font-mono font-bold ${winPct > 15 ? 'text-amber-600' : winPct > 5 ? 'text-gray-700' : 'text-gray-400'}`}>
                        {winPct}%
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center font-mono text-gray-400">{rh.pct_non_winners_when_violated}%</td>
                    <td className="px-3 py-2 w-32">
                      {rh.total_draws_violated > 0 && (
                        <div className="flex h-3 rounded-full overflow-hidden bg-gray-200">
                          <div className="bg-amber-500" style={{ width: `${winPct}%` }} title={`${winPct}% winners`} />
                          <div className="bg-gray-300" style={{ width: `${rh.pct_non_winners_when_violated}%` }} title={`${rh.pct_non_winners_when_violated}% non-winners`} />
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Insight */}
        {violated_rules.length > 0 ? (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            <span className="font-bold">Current Draw Violations:</span> {violated_rules.map(r => RULE_LABELS[r] || r).join(', ')}.
            {' '}Historically, when these rules are violated, the draw is a winner{' '}
            <strong>
              {(() => {
                const relevantRules = rule_history.filter(rh => rh.is_violated_now && rh.total_draws_violated > 0);
                if (relevantRules.length === 0) return '0%';
                const avgPct = relevantRules.reduce((sum, rh) => sum + rh.pct_winners_when_violated, 0) / relevantRules.length;
                return `~${avgPct.toFixed(1)}%`;
              })()}
            </strong> of the time.
          </div>
        ) : (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-emerald-800">
            <span className="font-bold">No rule violations</span> in the current draw — all rules within normal bounds.
          </div>
        )}
      </div>

      {/* Point 3: When these rules are flouted, what kind of next draw follows? */}
      {data.next_draw_prediction?.stats?.total_similar > 0 && (
        <div className="bg-slate-800/60 rounded-xl p-5 border border-white/10" data-testid="next-draw-prediction">
          <h2 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
            <ArrowRight size={16} weight="bold" className="text-purple-400" />
            Next Draw Prediction — When {violated_rules.map(r => RULE_LABELS[r]).join(' + ')} Are Flouted Together
          </h2>
          <p className="text-[10px] text-white/40 mb-4">
            Found {data.next_draw_prediction.stats.total_similar} historical draws with the same rule violations. Here's what the NEXT draw looked like:
          </p>

          {/* Stats summary */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <p className="text-[9px] text-white/40 uppercase font-bold">Historical Matches</p>
              <p className="text-xl font-bold text-white">{data.next_draw_prediction.stats.total_similar}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <p className="text-[9px] text-white/40 uppercase font-bold">Avg Next Sum</p>
              <p className="text-xl font-bold text-cyan-400">{data.next_draw_prediction.stats.avg_sum}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <p className="text-[9px] text-white/40 uppercase font-bold">Sum Range</p>
              <p className="text-xl font-bold text-white">{data.next_draw_prediction.stats.min_sum}–{data.next_draw_prediction.stats.max_sum}</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <p className="text-[9px] text-white/40 uppercase font-bold">Next Was Winner</p>
              <p className="text-xl font-bold text-amber-400">{data.next_draw_prediction.stats.winners_in_next}</p>
              <p className="text-[9px] text-amber-300">{data.next_draw_prediction.stats.pct_winners}%</p>
            </div>
            <div className="bg-white/5 rounded-lg p-3 text-center">
              <p className="text-[9px] text-white/40 uppercase font-bold">No Winner</p>
              <p className="text-xl font-bold text-white/60">{data.next_draw_prediction.stats.total_similar - data.next_draw_prediction.stats.winners_in_next}</p>
              <p className="text-[9px] text-white/40">{(100 - data.next_draw_prediction.stats.pct_winners).toFixed(1)}%</p>
            </div>
          </div>

          {/* What rules did the NEXT draw typically violate? */}
          <div className="mb-4">
            <p className="text-[10px] text-white/50 font-bold uppercase mb-2">What the Next Draw Typically Violated:</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.next_draw_prediction.stats.next_rule_violations || {})
                .sort((a, b) => b[1].count - a[1].count)
                .map(([r, v]) => (
                  <span key={r} className={`px-2.5 py-1 rounded text-[10px] font-bold ${
                    v.pct > 20 ? 'bg-red-500/30 text-red-300 border border-red-500/40' :
                    v.pct > 10 ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30' :
                    v.count > 0 ? 'bg-white/10 text-white/50 border border-white/10' : 'bg-white/5 text-white/20'
                  }`}>
                    {RULE_LABELS[r]}: {v.count} ({v.pct}%)
                  </span>
                ))}
            </div>
          </div>

          {/* Historical similar draws table */}
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-[10px]">
              <thead className="sticky top-0 bg-slate-800 z-10">
                <tr className="border-b border-white/20">
                  <th className="px-2 py-1.5 text-left text-white/50 font-bold">Trigger Date</th>
                  <th className="px-2 py-1.5 text-left text-white/50 font-bold">Trigger Balls</th>
                  <th className="px-2 py-1.5 text-center text-white/30 font-bold"><ArrowRight size={12} className="inline" /></th>
                  <th className="px-2 py-1.5 text-left text-purple-400 font-bold">Next Date</th>
                  <th className="px-2 py-1.5 text-left text-purple-400 font-bold">Next Balls</th>
                  <th className="px-2 py-1.5 text-left text-purple-400 font-bold">LS</th>
                  <th className="px-2 py-1.5 text-center text-purple-400 font-bold">Sum</th>
                  <th className="px-2 py-1.5 text-center text-amber-400 font-bold">Winner?</th>
                  <th className="px-2 py-1.5 text-left text-purple-400 font-bold">Next Violations</th>
                </tr>
              </thead>
              <tbody>
                {data.next_draw_prediction.similar_draws.map((sd, i) => (
                  <tr key={`sd-${sd.trigger_date || ''}-${i}`} className={`border-b border-white/5 ${sd.next_has_winner ? 'bg-amber-500/10' : ''}`}>
                    <td className="px-2 py-1 text-white/50 whitespace-nowrap">{sd.trigger_date}</td>
                    <td className="px-2 py-1">
                      <div className="flex gap-0.5">
                        {sd.trigger_balls.map((b, j) => <span key={`sd-${i}-${b}-${j}`} className="bg-white/10 px-1 rounded text-white/60 font-mono">{b}</span>)}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-center text-white/20"><ArrowRight size={10} /></td>
                    <td className="px-2 py-1 text-white font-bold whitespace-nowrap">{sd.next_date}</td>
                    <td className="px-2 py-1">
                      <div className="flex gap-0.5">
                        {sd.next_balls.map((b, j) => <span key={`sd-nb-${i}-${b}-${j}`} className="bg-purple-500/20 px-1 rounded text-purple-300 font-mono">{b}</span>)}
                      </div>
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex gap-0.5">
                        {sd.next_ls.map((b, j) => <span key={`sd-nl-${i}-${b}-${j}`} className="bg-amber-500/20 px-1 rounded text-amber-300 font-mono">{b}</span>)}
                      </div>
                    </td>
                    <td className="px-2 py-1 text-center font-mono text-white/70">{sd.next_sum}</td>
                    <td className="px-2 py-1 text-center">
                      {sd.next_has_winner
                        ? <span className="bg-amber-500/30 text-amber-300 px-1.5 py-0.5 rounded text-[9px] font-bold">{formatAmount(sd.next_winner_amount)}</span>
                        : <span className="text-white/20">—</span>}
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex flex-wrap gap-0.5">
                        {sd.next_violated.map(r => (
                          <span key={r} className="bg-red-500/20 text-red-300 px-1 py-0.5 rounded text-[8px] font-bold">{RULE_LABELS[r]}</span>
                        ))}
                        {sd.next_violated.length === 0 && <span className="text-emerald-400 text-[8px]">clean</span>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Insight */}
          <div className="mt-4 bg-purple-900/20 border border-purple-500/30 rounded-lg p-3 text-[11px] text-purple-200">
            <span className="font-bold">Pattern:</span> When {violated_rules.map(r => RULE_LABELS[r]).join(' + ')} are all flouted together,
            the next draw had an average sum of <strong>{data.next_draw_prediction.stats.avg_sum}</strong>,
            and <strong>{data.next_draw_prediction.stats.pct_winners}%</strong> of the time the next draw was a winner.
            Most common next violations: <strong>
              {Object.entries(data.next_draw_prediction.stats.next_rule_violations || {})
                .filter(([, v]) => v.pct > 10)
                .sort((a, b) => b[1].pct - a[1].pct)
                .slice(0, 3)
                .map(([r, v]) => `${RULE_LABELS[r]} (${v.pct}%)`)
                .join(', ')}
            </strong>.
          </div>
        </div>
      )}
    </div>
  );
}

function AnalysisResults() {
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/analysis/winner-analysis`);
        setAnalysis(res.data);
      } catch { toast.error('Failed to fetch analysis'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-white/50 text-center py-10">Computing analysis across all segments...</div>;
  if (!analysis) return null;

  const { rule_summary, winner_rule_analysis, total_non_winner_draws, total_segments } = analysis;

  return (
    <div className="space-y-6">
      {/* 1. Categorized Rules + 2. % of each rule flouted */}
      <div className="bg-slate-800/60 rounded-xl p-5 border border-white/10" data-testid="rule-category-table">
        <h2 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
          <ChartBar size={16} weight="fill" className="text-cyan-400" />
          Rule Violation Categories &amp; Percentages
        </h2>
        <p className="text-[10px] text-white/40 mb-4">Across {total_non_winner_draws} non-winning draws in {total_segments} segments between winners</p>

        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/20">
                <th className="px-3 py-2 text-left text-white/50 font-bold">Rule</th>
                <th className="px-3 py-2 text-center text-white/50 font-bold">Total Violations</th>
                <th className="px-3 py-2 text-center text-white/50 font-bold">% of Draws</th>
                <th className="px-3 py-2 text-left text-white/50 font-bold">Draw %</th>
                <th className="px-3 py-2 text-center text-white/50 font-bold">Segments Hit</th>
                <th className="px-3 py-2 text-center text-white/50 font-bold">% of Segments</th>
                <th className="px-3 py-2 text-center text-amber-400 font-bold">Winner Also Violates</th>
                <th className="px-3 py-2 text-center text-amber-400 font-bold">Winner %</th>
              </tr>
            </thead>
            <tbody>
              {rule_summary.map((r, i) => {
                const barWidth = Math.min(r.pct_of_draws, 100);
                return (
                  <tr key={r.rule} className={`border-b border-white/5 ${i < 4 ? 'bg-white/5' : ''}`} data-testid={`rule-row-${r.rule}`}>
                    <td className="px-3 py-2">
                      <span className="font-bold text-white">{RULE_LABELS[r.rule] || r.rule}</span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`font-mono font-bold ${r.total_violations > 100 ? 'text-red-400' : r.total_violations > 50 ? 'text-amber-400' : 'text-white/60'}`}>
                        {r.total_violations}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`font-mono font-bold ${r.pct_of_draws > 15 ? 'text-red-400' : r.pct_of_draws > 5 ? 'text-amber-300' : 'text-white/50'}`}>
                        {r.pct_of_draws}%
                      </span>
                    </td>
                    <td className="px-3 py-2 w-40">
                      <div className="w-full bg-white/10 rounded-full h-2.5">
                        <div className={`h-2.5 rounded-full ${r.pct_of_draws > 15 ? 'bg-red-500' : r.pct_of_draws > 5 ? 'bg-amber-500' : 'bg-cyan-500'}`} style={{ width: `${barWidth}%` }} />
                      </div>
                    </td>
                    <td className="px-3 py-2 text-center text-white/60 font-mono">{r.segments_with_violation}/{total_segments}</td>
                    <td className="px-3 py-2 text-center text-white/60 font-mono">{r.pct_of_segments}%</td>
                    <td className="px-3 py-2 text-center">
                      <span className={`font-mono font-bold ${r.winner_also_violates > 20 ? 'text-red-400' : r.winner_also_violates > 5 ? 'text-amber-400' : 'text-white/50'}`}>
                        {r.winner_also_violates}/{total_segments}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={`font-mono font-bold ${r.winner_violates_pct > 10 ? 'text-red-400' : r.winner_violates_pct > 3 ? 'text-amber-300' : 'text-emerald-400'}`}>
                        {r.winner_violates_pct}%
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Key insight */}
        <div className="mt-4 bg-indigo-900/30 border border-indigo-500/30 rounded-lg p-3 text-[11px] text-indigo-200">
          <span className="font-bold">Insight:</span> Top violated rules between winners: <strong>{rule_summary.slice(0, 3).map(r => `${RULE_LABELS[r.rule]} (${r.pct_of_draws}%)`).join(', ')}</strong>.
          {' '}Winners rarely carry the same violations — most rules get "corrected" in winning draws.
        </div>
      </div>

      {/* 3. Does the winning draw flout the same rules? — Per winner breakdown */}
      <div className="bg-slate-800/60 rounded-xl p-5 border border-white/10" data-testid="winner-rule-match">
        <h2 className="text-sm font-bold text-white mb-1 flex items-center gap-2">
          <Trophy size={16} weight="fill" className="text-amber-400" />
          Winner Draw vs Segment Rule Violations
        </h2>
        <p className="text-[10px] text-white/40 mb-2">For each winning draw: which rules were flouted in the segment leading to it, and did the winner also flout them?</p>

        <div className="flex gap-4 mb-4 text-[10px]">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-red-500/60 border border-red-400" /> Matched — same rule flouted in both segment &amp; winner</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-emerald-500/60 border border-emerald-400" /> Corrected — segment flouted but winner did NOT</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded bg-purple-500/60 border border-purple-400" /> New — winner flouted but segment did NOT</span>
        </div>

        <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
          <table className="w-full text-[10px]">
            <thead className="sticky top-0 bg-slate-800 z-10">
              <tr className="border-b border-white/20">
                <th className="px-2 py-2 text-left text-white/50 font-bold">#</th>
                <th className="px-2 py-2 text-left text-white/50 font-bold">Winner Date</th>
                <th className="px-2 py-2 text-right text-white/50 font-bold">Jackpot</th>
                <th className="px-2 py-2 text-left text-white/50 font-bold">Balls</th>
                <th className="px-2 py-2 text-center text-white/50 font-bold">Draws</th>
                <th className="px-2 py-2 text-left text-red-400 font-bold">Matched (both violated)</th>
                <th className="px-2 py-2 text-left text-emerald-400 font-bold">Corrected (segment only)</th>
                <th className="px-2 py-2 text-left text-purple-400 font-bold">New (winner only)</th>
              </tr>
            </thead>
            <tbody>
              {winner_rule_analysis.map((wa, i) => (
                <tr key={`wa-${wa.winner_date || ''}-${i}`} className="border-b border-white/5 hover:bg-white/5" data-testid={`winner-analysis-${i}`}>
                  <td className="px-2 py-2 text-white/40">{i + 1}</td>
                  <td className="px-2 py-2 text-white font-bold whitespace-nowrap">{wa.winner_date}</td>
                  <td className="px-2 py-2 text-right text-emerald-400 font-bold whitespace-nowrap">{formatAmount(wa.winner_amount)}</td>
                  <td className="px-2 py-2">
                    <div className="flex gap-0.5">
                      {wa.winner_balls.map((b, j) => <span key={`wa-b-${i}-${b}-${j}`} className="bg-white/10 px-1 rounded font-mono text-white/80">{b}</span>)}
                      <span className="text-white/20 mx-0.5">|</span>
                      {wa.winner_ls.map((b, j) => <span key={`wa-ls-${i}-${b}-${j}`} className="bg-amber-500/20 px-1 rounded font-mono text-amber-300">{b}</span>)}
                    </div>
                  </td>
                  <td className="px-2 py-2 text-center text-white/50 font-mono">{wa.draws_between}</td>
                  <td className="px-2 py-2">
                    {wa.matched_rules.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {wa.matched_rules.map(r => (
                          <span key={r} className="bg-red-500/30 text-red-300 px-1.5 py-0.5 rounded text-[9px] font-bold border border-red-500/40">
                            {RULE_LABELS[r] || r}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-white/20">none</span>}
                  </td>
                  <td className="px-2 py-2">
                    {wa.corrected_rules.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {wa.corrected_rules.map(r => (
                          <span key={r} className="bg-emerald-500/30 text-emerald-300 px-1.5 py-0.5 rounded text-[9px] font-bold border border-emerald-500/40">
                            {RULE_LABELS[r] || r}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-white/20">none</span>}
                  </td>
                  <td className="px-2 py-2">
                    {wa.new_violations.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {wa.new_violations.map(r => (
                          <span key={r} className="bg-purple-500/30 text-purple-300 px-1.5 py-0.5 rounded text-[9px] font-bold border border-purple-500/40">
                            {RULE_LABELS[r] || r}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-white/20">none</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Key insight box */}
        {(() => {
          const totalMatched = winner_rule_analysis.reduce((a, wa) => a + wa.matched_rules.length, 0);
          const totalCorrected = winner_rule_analysis.reduce((a, wa) => a + wa.corrected_rules.length, 0);
          const totalNew = winner_rule_analysis.reduce((a, wa) => a + wa.new_violations.length, 0);
          const totalAll = totalMatched + totalCorrected + totalNew;
          return (
            <div className="mt-4 bg-amber-900/20 border border-amber-500/30 rounded-lg p-3 text-[11px] text-amber-200">
              <span className="font-bold">Key Finding:</span> Across {winner_rule_analysis.length} winners:
              {' '}<span className="text-emerald-300 font-bold">{totalCorrected}</span> rules were corrected ({totalAll > 0 ? Math.round(totalCorrected / totalAll * 100) : 0}%),
              {' '}<span className="text-red-300 font-bold">{totalMatched}</span> carried over ({totalAll > 0 ? Math.round(totalMatched / totalAll * 100) : 0}%),
              {' '}<span className="text-purple-300 font-bold">{totalNew}</span> were new violations ({totalAll > 0 ? Math.round(totalNew / totalAll * 100) : 0}%).
              {' '}This means winning draws tend to <strong>correct</strong> the rules that were being flouted — the lottery "resets" pattern violations when someone wins.
            </div>
          );
        })()}
      </div>
    </div>
  );
}

export default function WinnerData() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState('data');

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/analysis/winner-data`);
        setData(res.data);
      } catch { toast.error('Failed to fetch winner data'); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="text-gray-400">Loading winner data...</div></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-6 py-4 sticky top-0 z-40 shadow-sm">
        <div className="max-w-[1600px] mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/')} className="text-gray-400 hover:text-gray-700 transition-colors" data-testid="back-btn">
            <ArrowLeft size={20} />
          </button>
          <div className="flex items-center gap-3">
            <Trophy size={28} weight="fill" className="text-amber-500" />
            <div>
              <h1 className="text-xl font-bold text-gray-900">W45: Winner Data</h1>
              <p className="text-xs text-gray-400">Jackpot winners & rule violation analysis between winning draws</p>
            </div>
          </div>
          {data && (
            <div className="ml-auto flex items-center gap-4 text-sm">
              <span className="bg-gray-100 px-3 py-1.5 rounded font-bold text-gray-600">{data.total_draws} draws</span>
              <span className="bg-amber-100 px-3 py-1.5 rounded font-bold text-amber-700">{data.total_winners} winners</span>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 py-6 space-y-4">


        <WindowNarrative windowId="W45" {...NARRATIVES.W45} className="mb-5" />
        {/* Tab selector */}
        <div className="flex gap-2" data-testid="winner-tabs">
          <button onClick={() => setTab('data')} className={`px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${tab === 'data' ? 'bg-amber-500 text-white shadow-lg' : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'}`} data-testid="tab-data">
            <Trophy size={15} weight="fill" className="inline mr-1.5" />Winner Data & Segments
          </button>
          <button onClick={() => setTab('analysis')} className={`px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${tab === 'analysis' ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'}`} data-testid="tab-analysis">
            <ChartBar size={15} weight="fill" className="inline mr-1.5" />Analysis Results
          </button>
          <button onClick={() => setTab('current')} className={`px-4 py-2.5 rounded-lg text-sm font-bold transition-all ${tab === 'current' ? 'bg-purple-600 text-white shadow-lg' : 'bg-white text-gray-500 hover:bg-gray-100 border border-gray-200'}`} data-testid="tab-current">
            <Warning size={15} weight="fill" className="inline mr-1.5" />Current Draw Analysis
          </button>
        </div>

        {tab === 'analysis' && <AnalysisResults />}
        {tab === 'current' && <CurrentDrawAnalysis />}

        {tab === 'data' && <>
        {/* Winners summary */}
        {data?.winners && (
          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Trophy size={16} weight="fill" className="text-amber-500" />
              All Jackpot Winners ({data.total_winners})
            </h2>
            <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-gray-50">
                  <tr className="border-b border-gray-200">
                    <th className="px-3 py-2 text-left text-gray-500 font-bold text-xs">#</th>
                    <th className="px-3 py-2 text-left text-gray-500 font-bold text-xs">Date</th>
                    <th className="px-3 py-2 text-right text-gray-500 font-bold text-xs">Jackpot</th>
                    <th className="px-3 py-2 text-left text-gray-500 font-bold text-xs">Balls</th>
                    <th className="px-3 py-2 text-left text-gray-500 font-bold text-xs">Lucky Stars</th>
                    <th className="px-3 py-2 text-center text-gray-500 font-bold text-xs">Sum</th>
                  </tr>
                </thead>
                <tbody>
                  {data.winners.map((w, i) => (
                    <tr key={`w-${w.draw_date || ''}-${i}`} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`winner-row-${i}`}>
                      <td className="px-3 py-2 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-3 py-2 text-gray-900 font-bold whitespace-nowrap">{w.draw_date}</td>
                      <td className="px-3 py-2 text-right text-emerald-600 font-bold">{formatAmount(w.amount)}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {w.balls.map((b, j) => <span key={`w-b-${i}-${b}-${j}`} className="bg-gray-200 px-1.5 py-0.5 rounded font-mono text-gray-700 text-xs">{b}</span>)}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {w.lucky_stars.map((b, j) => <span key={`w-ls-${i}-${b}-${j}`} className="bg-amber-100 px-1.5 py-0.5 rounded font-mono text-amber-700 text-xs">{b}</span>)}
                        </div>
                      </td>
                      <td className="px-3 py-2 text-center text-gray-500 font-mono">{w.balls.reduce((a, b) => a + b, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div>
          <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
            <Warning size={16} weight="fill" className="text-red-500" />
            Rule Violations Between Winning Draws ({data?.segments?.length || 0} segments)
          </h2>
          <div className="space-y-2">
            {data?.segments?.map((seg, i) => (
              <SegmentCard key={`seg-${seg?.start_date || ''}-${i}`} segment={seg} idx={i} />
            ))}
          </div>
        </div>
        </>}
      </main>
    </div>
  );
}
